import { Dispatch } from 'redux';
import { message } from 'antd';
import { localizedString } from '@locale/locale';
import {
  API
} from '@config/index';
import {
  EntityInfo,
  ResponseConfig,
  ParameterType,
  Attribute,
  TrainingPhraseConfig,
  FrameType,
  trainingPhraseToLocal,
  attributeToLocal,
  intentInfoToLocal,
  valueSuggestionToLocal,
  dataQueryToLocal,
  responseToLocal,
  valueAssociationToLocal,
  valueCheckToLocal,
  DefaultElseBranch,
  confirmationInfoToLocal,
  originKeyToLocal,
  DataQuery,
  genericTypeToLocal,
  externalTypeToLocal,
  serviceSlotToLocal,
  stateUpdateInfoToLocal,
  frameMappingToLocal,
  referenceNodeToLocal,
  actionToLocal,
  dialogActToLocal,
  postFillActionToLocal,
  DefaultGenericTypes,
  genericTypesToLocal,
  ParameterConfig,
  augmentMetaToLocal
} from '@interface/index';

import {
  ParameterMapStore,
  dataQueryToParameter,
  getAgentId,
  getFullName
} from '@lib/index';
import { EffectDeclare } from '@lib/model-proxy';
import { globalModel } from '@reducer/global';
import { store } from '@reducer/index';

export const effects: EffectDeclare = {};

effects.fetchIntent = ({ id, agentId }: { id: string, agentId?: string }) => {
  return async (dispatch: Dispatch) => {
    let payload: any = {};
    const res: any = await API.intent.getIntentById(id);

    const trainingPhrasePayload: TrainingPhraseConfig[] = [];
    const negativePhrasePayload: TrainingPhraseConfig[] = [];
    const entailmentPhrasePayload: TrainingPhraseConfig[] = [];
    const abortPhrasePayload: TrainingPhraseConfig[] = [];
    const abortNegativePhrasePayload: TrainingPhraseConfig[] = [];
    const abortEntailmentPhrasePayload: TrainingPhraseConfig[] = [];
    // selected parameters
    const selectedParametersPayload: Attribute[] = [];
    const globalSelectedParametersPayload: Attribute[] = [];

    if (res.attributes && res.attributes.length
    ) {
      const kvs: { [key: string]: ParameterConfig } = {};

      (res.attributes || []).forEach((parameter: any) => {
        const target: Attribute | undefined = attributeToLocal(parameter);

        if (!target) {
          return;
        }

        if (target.isGlobal) {
          globalSelectedParametersPayload.push(target);
        } else {
          selectedParametersPayload.push(target);
        }

        kvs[target.id || ''] = target;
      });

      ParameterMapStore.setMany(kvs);
    }

    ((res && res.frame_expressions) || []).forEach((tp: any) => {
      const tpItemPayload = trainingPhraseToLocal(tp);
      trainingPhrasePayload.push(tpItemPayload);
    });
    ((res && res.negative_expressions) || []).forEach((tp: any) => {
      const tpItemPayload = trainingPhraseToLocal(tp);
      negativePhrasePayload.push(tpItemPayload);
    });

    ((res && res.entailment_expressions) || []).forEach((tp: any) => {
      const tpItemPayload = trainingPhraseToLocal(tp);
      entailmentPhrasePayload.push(tpItemPayload);
    });

    ((res && res.abort_expressions) || []).forEach((tp: any) => {
      const tpItemPayload = trainingPhraseToLocal(tp);
      abortPhrasePayload.push(tpItemPayload);
    });

    ((res && res.abort_negative_expressions) || []).forEach((tp: any) => {
      const tpItemPayload = trainingPhraseToLocal(tp);
      abortNegativePhrasePayload.push(tpItemPayload);
    });

    ((res && res.abort_entailment_expressions) || []).forEach((tp: any) => {
      const tpItemPayload = trainingPhraseToLocal(tp);
      abortEntailmentPhrasePayload.push(tpItemPayload);
    });


    const { responsePayload, elsePayload } = effects.fetchAgentResponses({
      intentId: id,
      agentResponses: res.responses || []
    });

    // DataQueries
    const dataQueriesPayload: DataQuery[] = [];
    const kvs: { [key: string]: ParameterConfig } = {};

    (res.data_queries || []).forEach((item: any) => {
      const data: DataQuery = dataQueryToLocal(item);
      dataQueriesPayload.push(data);
      kvs[data.id || ''] = dataQueryToParameter(data);
    });
    ParameterMapStore.setMany(kvs);

    const valueAssociations = (res.associations || [])
      .map(valueAssociationToLocal);

    payload = {
      valueAssociations,
      id: res.id,
      name: res.name || '',
      comment: res.comment,
      source: res.source,
      nestedSlotCustomizations: res.nested_slot_customizations && res.nested_slot_customizations.map(attributeToLocal),
      originKey: res.origin_key && originKeyToLocal(res.origin_key),
      structKey: res.struct_key && originKeyToLocal(res.struct_key),
      branching: res.branching,
      webHookUrl: res.suggest_webhook || '',
      trainingPhrases: trainingPhrasePayload,
      negativePhrases: negativePhrasePayload,
      entailmentPhrases: entailmentPhrasePayload,
      abortPhrases: abortPhrasePayload,
      abortNegativePhrases: abortNegativePhrasePayload,
      abortEntailmentPhrases: abortEntailmentPhrasePayload,
      selectedParameters: selectedParametersPayload,
      globalSelectedParameters: globalSelectedParametersPayload,
      dataQueries: dataQueriesPayload,
      ...responsePayload,
      ...elsePayload,
      services: (res.services || []).map(serviceSlotToLocal),
      selectedCompositeInherit: (res.inherited_frames || []).map(
        (item: any) => ({
          id: item.frame.id,
          name: item.frame.name,
          type: item.frame.type,
          state: item.state
        })
      ),
      genericTypes: genericTypesToLocal(res.generic_types),
      externalTypes: (res.external_types || []).map(externalTypeToLocal),
      valueSuggestions: (res.value_suggestions || []).map(valueSuggestionToLocal),
      confirmations: (res.confirmations || []).map(confirmationInfoToLocal),
      valueChecks: (res.value_checks || []).map(valueCheckToLocal),
      stateUpdates: (res.state_updates || []).map(stateUpdateInfoToLocal),
      mappings: (res.mappings || []).map(frameMappingToLocal),
      cronData: res.cron_data && {
        dataQueryId: res.cron_data.data_query_id,
        schedule: res.cron_data.schedule
      },
      dialogActs: (res.dialog_acts || []).map(dialogActToLocal),
      postFillActions: (res.post_fill_actions || []).map(postFillActionToLocal),
      triggers: res.triggers || [],
      dialogAct: res.dialog_act,
      description: res.description,
      qualification: res.qualification,
      externalQualification: res.external_qualification,
      triggeredByDU: res.triggered_by_du,
      booleanGatePrompts: res.boolean_gate_prompts,
      booleanGateExpressionsYes: res.boolean_gate_expressions_yes &&
        res.boolean_gate_expressions_yes.map(trainingPhraseToLocal),
      booleanGateExpressionsNo: res.boolean_gate_expressions_no &&
        res.boolean_gate_expressions_no.map(trainingPhraseToLocal),
      enableFallback: res.enable_fallback,
      fallbackInfo: res.fallback_info && augmentMetaToLocal(res.fallback_info),
      soft: res.soft,
      softResponse: res.soft_response && augmentMetaToLocal(res.soft_response)
    };

    setTimeout(() => {
      const parsedIntent = intentInfoToLocal(res);

      store.dispatch({
        payload: parsedIntent,
        type: 'global/intent_update'
      });

      dispatch({
        payload,
        type: 'intent/update_state'
      });


      // force update neccessary fields
      const param = ParameterMapStore.get(payload.id);
      ParameterMapStore.set(payload.id, {
        ...param,
        description: payload.description
      });


      window.sessionStorage.setItem('curFrame', JSON.stringify({
        id: payload.id,
        name: payload.name,
        frameType: FrameType.intent,
        attributes: [
          ...payload.selectedParameters,
          ...payload.dataQueries
        ].map((attr: Attribute) =>
          attr.id)
      }));
    }, 1000);

    return {
      ...responsePayload,
      ...elsePayload,
      ...payload
    };
  };
};

effects.fetchParameters = ({ id, agentId }: { id: string, agentId?: string }) => {
  return async (dispatch: Dispatch) => {
    const { parameterPayload }: any = await dispatch<any>(
      globalModel.effects.fetchParameters({ id: agentId || getAgentId() })
    );

    return parameterPayload;
  };
};

effects.fetchAgentResponses = ({
  agentResponses
}: {
  agentResponses: any[];
}) => {

  let payload: any = {};
  let elsePayload: any = {};
  const agentRes = agentResponses;

  const agentResponsesPayload = agentRes.map(responseToLocal);

  const whenFilter = (item: ResponseConfig) => {
    return !item.isElseBranch;
  };
  const elseFilter = (item: ResponseConfig) => {
    return item.isElseBranch;
  };

  payload = {
    agentResponses: agentResponsesPayload.filter(whenFilter)
  };

  elsePayload = {
    elseResponses: agentResponsesPayload.filter(elseFilter).length
      ? agentResponsesPayload.filter(elseFilter)
      : [DefaultElseBranch]
  };

  return {
    elsePayload,
    responsePayload: payload
  };
};

effects.patchIntent = (
  { id, intentPayload, agentId }: { id: string; intentPayload: any; agentId?: string; },
  callback?: () => void
) => {
  return async (dispatch: Dispatch) => {
    const res: any = await API.intent
      .updateIntent({ metaID: id, agentID: agentId }, intentPayload)
      .catch((err: any) => {
        if (err.message) {
          message.warning(err.message);
        } else {
          message.warning(localizedString('common_save_failed'));
        }

        dispatch({
          type: 'intent/update_state',
          payload: {
            loading: false
          }
        });
        throw err;
      });

    dispatch({
      type: 'intent/update_state',
      payload: {
        loading: false,
        dirty: false
      }
    });
    if (callback) {
      callback();
    }
  };
};
